import { useMemo, useState } from 'react';

import {
  Accordion,
  Badge,
  Button,
  IconButton,
  PageHeader,
  Stack,
  Table,
  Text,
  tokens,
  Tooltip,
  useNotify,
} from '@hotjar/hotsauce';
import { useTranslationWithPrefix, useHelpHotjarUrl } from '@hotjar/translations';
import { useWindowTitle } from '@hotjar/webapp/hooks/useWindowTitle';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { TEAM } from '@hotjar/webapp/constants/routes';

import { useCurrentUser } from '@hotjar/user';
import type {
  Organization,
  OrganizationAccess,
  OrganizationId,
  OrganizationMember,
} from '@hotjar/organization';
import {
  useOrganizations,
  ORGANIZATIONS_QUERY_KEY,
  revokeOrganizationUserAccess,
  updateOrganizationUserAccess,
} from '@hotjar/organization';
import { logError } from '@hotjar/insights-common/lib/logger';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { SearchProvider } from '@hotjar/webapp/components/SearchProvider/SearchProvider';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import { useSites } from '@hotjar/site';
import { GrantAccessModal } from '@hotjar/webapp/features/Activation/GrantAccessModal';

import * as S from './styles';
import { PERMISSIONS_LEARN_MORE_LINK, TRANSLATION_DOMAIN } from './constants';
import { TeamListPageLoader } from './components/TeamListPageLoader';
import { TeamListUserRow } from './components/TeamListUserRow';
import { RevokeUserAccessModal } from './components/RevokeUserAccessModal';
import { addSitesToOrganizations } from '../../SitesAndOrganizations/utils';
import type { OrganizationListItem } from './types';

interface RevokeUserAccessData {
  organization: Organization;
  user: OrganizationMember;
}

export function TeamListPage(): JSX.Element {
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);

  useWindowTitle(t('title'));

  const helpHotjarUrl = useHelpHotjarUrl();
  const { showToast } = useNotify();

  const { data: currentUser, isLoading: isLoadingCurrentUser } = useCurrentUser();
  const { data: organizations = [], isLoading: isLoadingOrganizations } = useOrganizations(
    currentUser?.id,
  );
  const { query: sitesQuery } = useSites();
  const { data: sites, isLoading: isLoadingSites } = sitesQuery;

  const isLoading = isLoadingCurrentUser || isLoadingOrganizations || isLoadingSites;

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userAccessRevokeData, setUserAccessRevokeData] = useState<RevokeUserAccessData | null>(
    null,
  );
  const [isDeleting, setDeleting] = useState(false);
  const [filteredOrganizations, setFilteredOrganizations] = useState<Organization[]>([]);

  useEffectOnceWithDependencies(
    () => {
      setFilteredOrganizations(organizations);
    },
    [organizations],
    () => !!organizations.length,
  );

  const onSearchChanged = (searchValue = '') => {
    const normalizedSearchValue = searchValue.toLowerCase().trim();

    if (!normalizedSearchValue.length) {
      setFilteredOrganizations(organizations);
      return;
    }

    const filteredOrganizationsById: Record<OrganizationId, Organization> = {};

    organizations.forEach((organization) => {
      // Organization name matches searchValue
      if (organization.name.toLowerCase().includes(normalizedSearchValue)) {
        filteredOrganizationsById[organization.id] = organization;
      }

      // A user's name or email matches searchValue
      const matchedUsers = organization.users.filter(
        (user) =>
          user.name.toLowerCase().includes(normalizedSearchValue) ||
          user.email?.toLowerCase().includes(normalizedSearchValue),
      );

      /**
       * Found results for users, but organization is not included in results so far. Let's include it, but only
       * with the users matched.
       */
      if (matchedUsers.length && !filteredOrganizationsById[organization.id]) {
        filteredOrganizationsById[organization.id] = {
          ...organization,
          users: matchedUsers,
        };
      }
    });

    setFilteredOrganizations(Object.values(filteredOrganizationsById));
  };

  const organizationsWithSites = useMemo(() => {
    return addSitesToOrganizations(filteredOrganizations, sites) as Array<OrganizationListItem>;
  }, [filteredOrganizations, sites]);

  const onAccessLevelChange = async (
    organization: Organization,
    user: OrganizationMember,
    newAccessLevel: OrganizationAccess,
  ) => {
    try {
      await updateOrganizationUserAccess(organization.id, user.id, newAccessLevel);

      showToast({
        kind: 'success',
        text: t('edit-user.success'),
      });
    } catch (error) {
      showToast({
        kind: 'danger',
        text: t('edit-user.error'),
      });

      if (error instanceof Error) {
        logError(
          new Error(
            `Failed to update user ${user.id} access level to ${newAccessLevel} in organization ${organization.id}. Error message: ${error.message}`,
          ),
        );
      }
    }
  };

  const onRevokeAccessClick = (organization: Organization, user: OrganizationMember) => {
    setUserAccessRevokeData({
      organization,
      user,
    });
    setDeleteModalOpen(true);
  };

  const onCancelRevokeAccess = () => {
    setDeleting(false);
    setUserAccessRevokeData(null);
    setDeleteModalOpen(false);
  };

  const onConfirmRevokeAccess = async (organization: Organization, user: OrganizationMember) => {
    setDeleting(true);

    try {
      await revokeOrganizationUserAccess(organization.id, user.id);

      showToast({
        kind: 'success',
        text: t('remove-user.success'),
      });

      queryClient.invalidateQueries(ORGANIZATIONS_QUERY_KEY);
    } catch (error) {
      showToast({
        kind: 'danger',
        text: t('remove-user.error'),
      });

      if (error instanceof Error) {
        logError(
          new Error(
            `Failed to revoke user ${user.id} access to organization ${organization.id}. Error message: ${error.message}`,
          ),
        );
      }
    } finally {
      setDeleting(false);
      setUserAccessRevokeData(null);
      setDeleteModalOpen(false);
    }
  };

  const getCurrentUserAccessInOrganization = (
    organizationId: OrganizationId,
  ): string | undefined => {
    if (!currentUser || !organizations) {
      return undefined;
    }

    const unfilteredOrganizationObject = organizations.find(
      (unfilteredOrganization) => unfilteredOrganization.id === organizationId,
    );

    if (!unfilteredOrganizationObject) {
      return undefined;
    }

    const currentUserAccess = unfilteredOrganizationObject.users.find(
      (user) => user.id === currentUser.id,
    )?.access;

    if (!currentUserAccess) {
      return undefined;
    }

    return t(`access-level.${currentUserAccess}`);
  };

  return (
    <>
      <S.StyledPage>
        <Stack direction="column" spacing="x3">
          <PageHeader
            title={t('title')}
            actions={
              <Button
                as="a"
                icon="user_add"
                kind="primary"
                href={getRoute(TEAM.INVITE.path)}
                data-testid="team-list-invite-members-button"
              >
                {t('invite-team-members')}
              </Button>
            }
          />

          {isLoading ? (
            <TeamListPageLoader />
          ) : (
            <SearchProvider placeholder={t('search-placeholder')} onSearchChanged={onSearchChanged}>
              {organizationsWithSites.length ? (
                <Accordion sectionToShow={0}>
                  {organizationsWithSites?.map((organization) => (
                    <Accordion.Section
                      key={organization.id}
                      icon="building_business"
                      title={
                        <S.FlexContainerCenter>
                          <Text textType="subtitle1">{organization.name}</Text>

                          {currentUser?.id ? (
                            <Badge
                              kind="custom"
                              size="s"
                              color={tokens.color.ui_disabled}
                              textColor={tokens.color.text_light}
                            >
                              {getCurrentUserAccessInOrganization(organization.id)}
                            </Badge>
                          ) : null}
                        </S.FlexContainerCenter>
                      }
                    >
                      <Stack
                        direction="column"
                        spacing="x3"
                        data-testid={`team-list-org-${organization.id}`}
                      >
                        <Table
                          caption={t('organization-users', { organizationName: organization.name })}
                          tableName={t('organization-users', {
                            organizationName: organization.name,
                          })}
                        >
                          <Table.Head>
                            <Table.HeadRow>
                              <S.TableHeadCellVerticalCenter>
                                {t('full-name')}
                              </S.TableHeadCellVerticalCenter>
                              <S.TableHeadCellVerticalCenter>
                                {t('email')}
                              </S.TableHeadCellVerticalCenter>
                              <S.TableHeadCellVerticalCenter>
                                <S.FlexContainerCenter>
                                  <Text textType="subtitle2">{t('access')}</Text>
                                  <IconButton
                                    as="a"
                                    tooltipText={t('access-tooltip')}
                                    icon="info"
                                    kind="ghost"
                                    size="s"
                                    href={`${helpHotjarUrl}${PERMISSIONS_LEARN_MORE_LINK}`}
                                    isExternal
                                    rel="noopener noreferrer"
                                  />
                                </S.FlexContainerCenter>
                              </S.TableHeadCellVerticalCenter>
                              <S.TableHeadCellVerticalCenter />
                            </Table.HeadRow>
                          </Table.Head>
                          <Table.Body>
                            {organization.users
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((user) => (
                                <TeamListUserRow
                                  key={user.id}
                                  currentUser={currentUser}
                                  organization={organization}
                                  user={user}
                                  onAccessLevelChange={onAccessLevelChange}
                                  onRevokeAccessClick={onRevokeAccessClick}
                                />
                              ))}
                          </Table.Body>
                        </Table>

                        <div>
                          {organization.sites.length ? (
                            <Button
                              as="a"
                              kind="secondary"
                              href={getRoute(TEAM.INVITE.path, { organizationId: organization.id })}
                            >
                              {t('invite-team-members-to-organization', {
                                organizationName: organization.name,
                              })}
                            </Button>
                          ) : (
                            <Tooltip text={t('invite-team-members-empty-org-tooltip')}>
                              <span>
                                <Button kind="secondary" isDisabled>
                                  {t('invite-team-members-to-organization', {
                                    organizationName: organization.name,
                                  })}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </Stack>
                    </Accordion.Section>
                  ))}
                </Accordion>
              ) : null}
            </SearchProvider>
          )}
        </Stack>
      </S.StyledPage>

      {userAccessRevokeData ? (
        <RevokeUserAccessModal
          isOpen={isDeleteModalOpen}
          isDeleting={isDeleting}
          organization={userAccessRevokeData.organization}
          user={userAccessRevokeData.user}
          onCancel={onCancelRevokeAccess}
          onConfirm={onConfirmRevokeAccess}
        />
      ) : null}

      <GrantAccessModal />
    </>
  );
}
